import { Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { cn } from '@apps/web/lib/utils';
import { useAllAdminOrganizations } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type AdminOrgPickerProps = {
    readonly value: string | undefined;
    readonly onValueChange: (value: string | undefined) => void;
    readonly triggerClassName?: string;
};

function getOrgLabel(org: {
    readonly internal_name?: string | null;
    readonly name: string;
}) {
    const internalName = org.internal_name?.trim();
    return internalName && internalName.length > 0 ? internalName : org.name;
}

/** Single-select searchable org picker for admin filter bars. */
export function AdminOrgPicker({
    value,
    onValueChange,
    triggerClassName,
}: AdminOrgPickerProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const { organizations, isLoading } = useAllAdminOrganizations({
        search,
        autoLoadPages: true,
    });

    const sorted = useMemo(
        () =>
            [...organizations].sort((a, b) =>
                getOrgLabel(a).localeCompare(getOrgLabel(b)),
            ),
        [organizations],
    );

    const labelMap = useMemo(() => {
        const map = new Map<string, string>();
        sorted.forEach((org) => map.set(org.id, getOrgLabel(org)));
        return map;
    }, [sorted]);

    // Keep the selected org's label even when the search term changes and the
    // selected org is no longer in the filtered results (which would otherwise
    // cause the trigger to revert to the "All Organizations" placeholder).
    const [cachedLabel, setCachedLabel] = useState<string | undefined>(
        undefined,
    );
    useEffect(() => {
        if (!value) {
            setCachedLabel(undefined);
            return;
        }
        const label = labelMap.get(value);
        if (label !== undefined) {
            setCachedLabel(label);
        }
    }, [value, labelMap]);

    const displayValue = value ? cachedLabel : undefined;

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="All Organizations"
            disabled={isLoading}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search organizations..."
            triggerClassName={triggerClassName ?? 'w-[250px]'}
            fullWidth={false}
            commandProps={{ shouldFilter: false }}
        >
            <CommandEmpty>
                {isLoading ? 'Loading…' : 'No organizations found.'}
            </CommandEmpty>
            <CommandGroup>
                <CommandItem
                    value="all"
                    onSelect={() => {
                        onValueChange(undefined);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            !value ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                    All Organizations
                </CommandItem>
                {sorted.map((org) => {
                    const label = labelMap.get(org.id) ?? getOrgLabel(org);
                    const internalName = org.internal_name?.trim();
                    const showSecondaryName =
                        internalName &&
                        internalName.length > 0 &&
                        internalName !== org.name;
                    const keywords = [org.name];
                    if (internalName) {
                        keywords.push(internalName);
                    }
                    return (
                        <CommandItem
                            key={org.id}
                            value={org.id}
                            keywords={keywords}
                            onSelect={(id) => {
                                onValueChange(id);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    value === org.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            {/* Wrap rather than truncate: the popover is only
                                as wide as its trigger, and agencies with long
                                or near-identical names are indistinguishable
                                once the label is clipped. */}
                            <div className="flex min-w-0 flex-col">
                                <span className="break-words">{label}</span>
                                {showSecondaryName && (
                                    <span className="text-muted-foreground text-xs break-words">
                                        {org.name}
                                    </span>
                                )}
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}
